import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaTruck, FaSearch, FaEnvelope, FaEye, FaTrash, FaEdit, FaUserCheck, FaPlus, FaTimes, FaSave, FaSpinner, FaPaperPlane } from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';
import ViewAsButton from '../components/ViewAsButton';
import InviteLinkModal from '../components/InviteLinkModal';
import { subcontractors } from '../api';

function SubbiesPage({ onLogout }) {
  const navigate = useNavigate();
  const [subbiesList, setSubbiesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingCount, setPendingCount] = useState(0);

  // Delete confirmation state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Create/Edit Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedSubbie, setSelectedSubbie] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    contact_person: '',
    email: '',
    phone: '',
    registration_number: '',
    address: ''
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchSubbies();
    fetchPendingCount();
  }, []);

  const fetchSubbies = async () => {
    setLoading(true);
    try {
      const data = await subcontractors.getAll();
      setSubbiesList(data.subcontractors || []);
    } catch (err) {
      console.error('Error fetching subcontractors:', err);
      toast.error('Failed to load subcontractors');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingCount = async () => {
    try {
      const response = await fetch(`${API_URL}/subcontractors/pending`);
      const data = await response.json();
      if (data.success) {
        setPendingCount(data.applications?.length || 0);
      }
    } catch (err) {
      console.error('Error fetching pending count:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      company_name: '',
      contact_person: '',
      email: '',
      phone: '',
      registration_number: '',
      address: ''
    });
    setSelectedSubbie(null);
  };

  const openCreateModal = () => {
    resetForm();
    setModalMode('create');
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (!formData.company_name || !formData.contact_person || !formData.email) {
        toast.error('Company name, contact person, and email are required');
        setSubmitting(false);
        return;
      }

      const payload = {
        company_name: formData.company_name,
        contact_person: formData.contact_person,
        email: formData.email,
        phone: formData.phone || null,
        registration_number: formData.registration_number || null,
        address: formData.address || null
      };

      const response = await fetch(`${API_URL}/subcontractors/admin-create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        toast.success(
          data.invite_email_sent
            ? `✅ Subcontractor "${formData.company_name}" created! Invite email sent to ${formData.email}.`
            : `✅ Subcontractor "${formData.company_name}" created! Note: invite email could not be sent (check SendGrid config).`
        );
        setShowModal(false);
        resetForm();
        fetchSubbies();
      } else {
        toast.error(data.error || 'Failed to create subcontractor');
      }
    } catch (err) {
      console.error('Error creating subcontractor:', err);
      toast.error('Failed to create subcontractor');
    } finally {
      setSubmitting(false);
    }
  };

  // ✅ Resend invite — emails a BRAND-NEW sign-in link (creates the login if missing)
  const [sendingInvite, setSendingInvite] = useState({});
  const [inviteInfo, setInviteInfo] = useState(null);

  const handleResendInvite = async (subbie) => {
    const email = subbie.contact_email || subbie.email;
    if (!email) {
      toast.warning('No email address on file for this subcontractor');
      return;
    }
    setSendingInvite(prev => ({ ...prev, [subbie.id]: true }));
    try {
      const response = await fetch(`${API_URL}/subcontractors/${subbie.id}/resend-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(
          data.email_sent
            ? `Invite re-sent to ${data.email || email}`
            : 'Login ready — email not configured, copy the link'
        );
        setInviteInfo({
          company: subbie.company_name,
          email: data.email || email,
          link: data.invite_link,
          emailSent: !!data.email_sent
        });
      } else {
        toast.error(data.error || 'Could not resend the invite');
      }
    } catch (err) {
      console.error('Error resending invite:', err);
      toast.error('Could not resend the invite');
    } finally {
      setSendingInvite(prev => ({ ...prev, [subbie.id]: false }));
    }
  };

  const handleEdit = (subbie) => {
    if (subbie && subbie.id) {
      navigate(`/subcontractors/${subbie.id}`);
    }
  };

  const handleDeleteClick = (subbie) => {
    setDeleteTarget(subbie);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    
    setDeleting(true);
    try {
      const response = await fetch(`${API_URL}/subcontractors/${deleteTarget.id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success(`✅ ${deleteTarget.company_name} deleted successfully`);
        setShowDeleteModal(false);
        setDeleteTarget(null);
        fetchSubbies();
      } else {
        toast.error(data.error || 'Failed to delete subcontractor');
      }
    } catch (err) {
      console.error('Error deleting subcontractor:', err);
      toast.error('Failed to delete subcontractor');
    } finally {
      setDeleting(false);
    }
  };

  const handleEmail = (subbie) => {
    if (subbie.contact_email) {
      window.location.href = `mailto:${subbie.contact_email}`;
    } else {
      toast.warning('No email address available');
    }
  };

  const filteredSubbies = subbiesList.filter(subbie =>
    subbie.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    subbie.contact_person?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    subbie.contact_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const styles = {
      'active': 'bg-green-100 text-green-600',
      'inactive': 'bg-red-100 text-red-600',
      'pending': 'bg-yellow-100 text-yellow-600',
      'suspended': 'bg-red-100 text-red-600',
      'approved': 'bg-green-100 text-green-600',
      'rejected': 'bg-red-100 text-red-600',
    };
    return `px-3 py-1 rounded-full text-xs font-semibold ${styles[status?.toLowerCase()] || styles.pending}`;
  };

  return (
    <AdminLayout activeTab="subbies" onLogout={onLogout} user={user}>
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-navy">All Subcontractors</h1>
          <p className="text-gray-500 text-sm">Manage all your subcontractors</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={openCreateModal}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              backgroundColor: '#00A86B',
              color: 'white',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              border: 'none',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#00995E'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#00A86B'}
          >
            <FaPlus size={14} /> Add Subcontractor
          </button>
          {(user.role === 'super_admin' || (user.permissions || []).includes('approve_subbies')) && (
            <Link 
              to="/admin/subbie-verification"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                backgroundColor: '#F59E0B',
                color: 'white',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                textDecoration: 'none',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              <FaUserCheck size={14} />
              Pending Verifications
              {pendingCount > 0 && (
                <span style={{
                  marginLeft: '4px',
                  backgroundColor: 'white',
                  color: '#F59E0B',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  padding: '2px 8px',
                  borderRadius: '9999px'
                }}>
                  {pendingCount}
                </span>
              )}
            </Link>
          )}
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search subcontractors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-green w-64"
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-4xl mb-4">🔄</div>
            <p className="text-gray-500">Loading subcontractors...</p>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Company</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Phone</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Trucks</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubbies.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                      No subcontractors found. Add your first subcontractor!
                    </td>
                  </tr>
                ) : (
                  filteredSubbies.map((subbie) => (
                    <tr key={subbie.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm font-medium text-navy">{subbie.company_name}</td>
                      <td className="px-6 py-3 text-sm text-gray-600">{subbie.contact_person || '-'}</td>
                      <td className="px-6 py-3 text-sm text-gray-600">{subbie.contact_phone || '-'}</td>
                      <td className="px-6 py-3 text-sm text-gray-600">{subbie.total_tractors || 0}</td>
                      <td className="px-6 py-3">
                        <span className={getStatusBadge(subbie.verification_status || subbie.status)}>
                          {subbie.verification_status || subbie.status || 'pending'}
                        </span>
                      </td>
                      <td className="px-6 py-3">
                        <div className="flex gap-2">
                          <Link 
                            to={`/subcontractors/${subbie.id}`}
                            className="w-8 h-8 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-blue-600 transition-colors flex items-center justify-center"
                            title="View"
                          >
                            <FaEye size={16} />
                          </Link>
                          <button 
                            onClick={() => handleEmail(subbie)}
                            className="w-8 h-8 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-green-600 transition-colors flex items-center justify-center"
                            title="Send Email"
                          >
                            <FaEnvelope size={16} />
                          </button>
                          <button 
                            onClick={() => handleResendInvite(subbie)}
                            disabled={sendingInvite[subbie.id]}
                            className="w-8 h-8 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-purple-600 transition-colors flex items-center justify-center disabled:opacity-50"
                            title="Resend invite (fresh sign-in link)"
                          >
                            {sendingInvite[subbie.id] ? <FaSpinner className="animate-spin" size={14} /> : <FaPaperPlane size={15} />}
                          </button>
                          <button 
                            onClick={() => handleEdit(subbie)}
                            className="w-8 h-8 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-yellow-600 transition-colors flex items-center justify-center"
                            title="Edit"
                          >
                            <FaEdit size={16} />
                          </button>
                          {/* Support tool: open the portal as this subbie (super admin only — a
                              subbie with no portal login yet simply gets no button). */}
                          <ViewAsButton subcontractorId={subbie.id} name={subbie.company_name} />
                          <button 
                            onClick={() => handleDeleteClick(subbie)}
                            className="w-8 h-8 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-red-600 transition-colors flex items-center justify-center"
                            title="Delete"
                          >
                            <FaTrash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteModal && deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowDeleteModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaTrash className="text-3xl text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-navy">Delete Subcontractor</h2>
              <p className="text-sm text-gray-500 mt-2">
                Are you sure you want to delete <strong className="text-navy">{deleteTarget.company_name}</strong>?
              </p>
              <p className="text-xs text-red-500 mt-1">
                This action cannot be undone. All associated data will be permanently removed.
              </p>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <span className="animate-spin">⏳</span> Deleting...
                  </>
                ) : (
                  <>
                    <FaTrash size={14} /> Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CREATE SUBBIE MODAL */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 pb-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-navy flex items-center gap-2">
                <FaTruck className="text-green-500" />
                Add New Subcontractor
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-navy transition-colors">
                <FaTimes size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                    placeholder="e.g. TPT Haulage"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Person <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="contact_person"
                    value={formData.contact_person}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                    placeholder="e.g. John Smith"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                      placeholder="subbie@company.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                      placeholder="+27 82 123 4567"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Registration Number
                  </label>
                  <input
                    type="text"
                    name="registration_number"
                    value={formData.registration_number}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                    placeholder="e.g. 2024/123456/07"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                    rows="2"
                    placeholder="Street address, city, province"
                  />
                </div>

                {/* ✅ Invite note - replaces the old temporary password field */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-700 flex items-start gap-2">
                    <span className="text-lg">📧</span>
                    <span>
                      <strong>No password needed.</strong> An invite email with a{" "}
                      <strong>"Set My Password"</strong> link will be sent to{" "}
                      <strong>{formData.email || 'the email above'}</strong>. The subcontractor sets
                      their own password to access the portal.
                    </span>
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 20px',
                    backgroundColor: submitting ? '#94A3B8' : '#00A86B',
                    color: 'white',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    border: 'none',
                    cursor: submitting ? 'default' : 'pointer'
                  }}
                  onMouseEnter={(e) => { if (!submitting) e.currentTarget.style.backgroundColor = '#00995E'; }}
                  onMouseLeave={(e) => { if (!submitting) e.currentTarget.style.backgroundColor = '#00A86B'; }}
                >
                  {submitting ? <FaSpinner className="animate-spin" size={14} /> : <FaSave size={14} />}
                  {submitting ? 'Creating...' : 'Create Subcontractor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <InviteLinkModal
        open={!!inviteInfo}
        onClose={() => setInviteInfo(null)}
        company={inviteInfo?.company}
        email={inviteInfo?.email}
        link={inviteInfo?.link}
        emailSent={inviteInfo?.emailSent}
      />
    </AdminLayout>
  );
}

export default SubbiesPage;